<template>
  <main class="screen scoresheet" @click="skip">
    <h2>Counting the money</h2>

    <ol class="scores">
      <li
        v-for="(row, i) in scores"
        :key="row.id"
        class="score"
        :class="scoreClasses(row, i)"
      >
        <template v-if="isRevealed(i)">
          <span class="score__rank">{{ row.rank }}</span>
          <span class="score__name">{{ row.name }}</span>
          <span class="score__parts">
            <span :title="`Cheques: ${money(row.chequeTotal)}`">
              <i class="cheque" aria-hidden="true" />{{ money(row.chequeTotal) }}
            </span>
            <span :title="`Unspent money: ${money(row.coins)}`">
              <i class="coin" aria-hidden="true" />{{ money(row.coins) }}
            </span>
          </span>
          <span class="score__total">{{ money(row.total) }}</span>
        </template>

        <!-- Face-down: position and nothing else. WHO holds each place is
             the whole suspense, so no rank, no name, no numbers. -->
        <template v-else>
          <span class="score__rank">?</span>
          <span class="score__name score__name--secret" aria-hidden="true">·····</span>
          <span class="score__total score__total--secret">$ —</span>
        </template>
      </li>
    </ol>

    <p v-if="stage === 'drumroll'" class="muted scoresheet__drumroll">And the richest is…</p>
    <p v-else-if="stage === 'winners' || stage === 'done'" class="muted scoresheet__headline">{{ headline }}</p>
    <p v-else class="muted scoresheet__hint">Tap to skip</p>

    <div v-if="stage === 'done'" class="screen__actions scoresheet__actions">
      <button v-if="!isOnline" class="primary" @click.stop="$store.dispatch('newGame', {})">Again</button>
      <button class="secondary" @click.stop="leave">{{ isOnline ? 'Back to the menu' : 'Done' }}</button>
    </div>
  </main>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { buildRevealPlan } from '@/utils/scoreReveal';

// Final scoring, IELLO p7: cheques plus every coin not spent. This is the
// first and only moment anyone's money or cheques are visible - see SeatList
// for why. The two parts are shown separately because the leftover-coins
// half is the one people forget is real: a dollar never bid is a dollar
// scored, and seeing it in its own column teaches that better than a rule
// ever did.
//
// The reveal is a SEQUENCE, not a dump - bug report 2026-08-28: "There
// should be more drama for revealing the final scores." Rows turn over from
// last place up (buildRevealPlan in utils/scoreReveal.js owns the order and
// the beats), a drumroll line lands before the winner, and the winner row
// gets the one gold flourish. A tap anywhere skips to the finished board,
// and prefers-reduced-motion never sees the sequence at all.
export default {
  name: 'ScoreSheet',
  data() {
    return {
      revealed: [], // indexes into `scores` that are face-up
      stage: 'counting', // counting -> drumroll -> winners -> done
      skipped: false, // suppresses the turn-over animations after a skip
    };
  },
  computed: {
    ...mapState(['game']),
    ...mapGetters(['scores', 'me', 'isOnline']),
    headline() {
      const winners = this.scores.filter((row) => row.isWinner);
      if (!winners.length) return '';
      if (winners.length > 1) {
        // IELLO p7: a tie on money goes to leftover coins, and one that
        // survives that is shared.
        const names = winners.map((w) => w.name);
        const list = names.length === 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
        return `${list} tie, coins and all.`;
      }
      const [winner] = winners;
      const mine = winner.id === this.me?.id;
      return mine
        ? `You end the richest, with ${this.money(winner.total)}.`
        : `${winner.name} ends the richest, with ${this.money(winner.total)}.`;
    },
  },
  mounted() {
    const reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      this.finish();
      return;
    }
    this.plan = buildRevealPlan(this.scores);
    this.stepIndex = 0;
    this.scheduleNext();
  },
  beforeUnmount() {
    if (this.timer) window.clearTimeout(this.timer);
  },
  methods: {
    money(thousands) {
      return `$${(thousands * 1000).toLocaleString('en-US')}`;
    },
    isRevealed(i) {
      return this.stage === 'done' || this.revealed.includes(i);
    },
    scoreClasses(row, i) {
      const up = this.isRevealed(i);
      return {
        'score--secret': !up,
        // The me-highlight and winner gold only land once a row is face-up,
        // or they would give the ending away.
        'score--me': up && row.id === this.me?.id,
        'score--winner': up && row.isWinner,
        'score--flip': up && !row.isWinner && !this.skipped,
        'score--fanfare': up && row.isWinner && !this.skipped,
      };
    },
    scheduleNext() {
      const step = this.plan?.[this.stepIndex];
      if (!step) return;
      this.timer = window.setTimeout(() => {
        this.revealed.push(...step.reveal);
        this.stage = step.kind === 'row' ? 'counting' : step.kind;
        this.stepIndex += 1;
        this.scheduleNext();
      }, step.after);
    },
    /** Tap anywhere: straight to the finished board. Idempotent once done. */
    skip() {
      if (this.stage === 'done') return;
      this.finish();
    },
    finish() {
      this.skipped = true; // everything lands at once - no turn-over animations
      if (this.timer) window.clearTimeout(this.timer);
      this.plan = null;
      this.revealed = this.scores.map((_, i) => i);
      this.stage = 'done';
    },
    leave() {
      if (this.isOnline) this.$store.dispatch('leaveRoom');
      else this.$store.dispatch('abandonGame');
    },
  },
};
</script>
